import { createTheme } from '@mui/material'
import { Theme as MuiTheme } from '@mui/material/styles'

import { colors } from './colors'
import MuiButton, { MuiButtonBase } from './components/MuiButton'
import MuiCheckbox from './components/MuiCheckbox'
import MuiFormLabel from './components/MuiFormLabel'
import MuiFormHelperText from './components/MuiFormHelperText'
import MuiInput from './components/MuiOutlinedInput'
import MuiPaper from './components/MuiPaper'
import MuiRadio from './components/MuiRadio'
import MuiList from './components/MuiList'
import MuiSwitch from './components/MuiSwitch'
import MuiTooltip from './components/MuiTooltip'
import MuiScopedCssBaseline from './components/MuiScopedCssBaseline'
import { backgrounds, boxShadows, fonts, palette, typography, typographyColors } from './consts'

declare module '@mui/material/styles' {
  interface Theme {
    colors: typeof colors
    boxShadows: typeof boxShadows
    backgrounds: typeof backgrounds
    typographyColors: typeof typographyColors
    fonts: typeof fonts
  }

  interface ThemeOptions {
    colors?: typeof colors
    boxShadows?: typeof boxShadows
    backgrounds?: typeof backgrounds
    typographyColors?: typeof typographyColors
    fonts?: typeof fonts
  }

  interface PaletteColor {
    secondary: string
    soft: string
    light: string
    opacity: string
  }

  interface SimplePaletteColorOptions {
    secondary?: string
    soft?: string
    light?: string
    opacity?: string
  }

  interface Palette {
    blue?: Palette['primary']
    lightBlue?: Palette['primary']
    red?: Palette['primary']
    lightRed?: Palette['primary']
    green?: Palette['primary']
    lightGreen?: Palette['primary']
    yellow?: Palette['primary']
    lightYellow?: Palette['primary']
    gray?: Palette['primary']
  }

  interface PaletteOptions {
    blue?: PaletteOptions['primary']
    lightBlue?: PaletteOptions['primary']
    red?: PaletteOptions['primary']
    lightRed?: PaletteOptions['primary']
    green?: PaletteOptions['primary']
    lightGreen?: PaletteOptions['primary']
    yellow?: PaletteOptions['primary']
    lightYellow?: PaletteOptions['primary']
    gray?: PaletteOptions['primary']
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    blue: true
    lightBlue: true
    red: true
    lightRed: true
    green: true
    lightGreen: true
    yellow: true
    lightYellow: true
    gray: true
  }
}
declare module '@emotion/react' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface Theme extends MuiTheme {}
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    title1: true
    title2: true
    title3: true
    title4: true
    small1: true
    body3: true
  }
}

export const theme = createTheme({
  components: {
    // These summaries contain editable fields and action buttons, so retain non-button wrappers.
    MuiAccordion: {
      defaultProps: { slots: { heading: 'div' } },
    },
    MuiAccordionSummary: {
      defaultProps: {
        component: 'div',
        nativeButton: false,
        slotProps: { content: { component: 'div' } },
      },
    },
    MuiScopedCssBaseline,
    MuiButton,
    MuiButtonBase,
    MuiCheckbox,
    MuiRadio,
    MuiSwitch,
    MuiInput,
    MuiOutlinedInput: MuiInput,
    MuiFormLabel,
    MuiFormHelperText,
    MuiList,
    MuiPaper,
    MuiTooltip,
  },
  palette,
  colors,
  boxShadows,
  backgrounds,
  typography,
  typographyColors,
  fonts,
})
